import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BottomNavigation from "@/components/BottomNavigation";
import ProjectForm from "@/components/project/ProjectForm";
import { useToast } from "@/hooks/use-toast";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { projectsAPI } from "@/api/projects";
import { useAuth } from "@/contexts/AuthContext";
import useSubscription from "@/contexts/SubscriptionContext";
import { isPremium } from "@/lib/featureGates";
import { Capacitor } from "@capacitor/core";
import MobileStoreRedirect from "@/components/ui/MobileStoreRedirect";
import { ScreenHeader } from "@/components/redesign";

const NewProjectPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { status, refreshSubscription } = useSubscription();
  const { isLoading: isSubscriptionLoading, projectLimit, has_access, projects_locked } = status;
  // NO_ACCESS only fires for premium-expired users; free users always have access.
  const isAccessDenied = has_access === false || projects_locked === true;
  const userIsPremium = isPremium(status);
  const isMobilePlatform = Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android';
  const [redirecting, setRedirecting] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [isSubscriptionLoadingFailed, setIsSubscriptionLoadingFailed] = useState(false);

  // Load subscription and project data with error handling
  useEffect(() => {
    // Flag to track if the component is mounted
    let isMounted = true;

    const loadData = async () => {
      try {
        // Load projects first to avoid multiple subscription checks
        if (isMounted) setIsLoadingProjects(true);
        const projectsData = await projectsAPI.getProjects();

        if (isMounted) {
          if (Array.isArray(projectsData)) {
            setProjects(projectsData);
          }
          setIsLoadingProjects(false);
        }

        // Then check subscription only once. The subscription context owns the
        // `isLoading` flag (destructured above as `isSubscriptionLoading`), so we
        // don't toggle it here — refreshSubscription() updates it in context.
        try {
          // Add timeout to prevent hanging
          const subscriptionPromise = refreshSubscription();
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Subscription check timed out')), 5000)
          );

          await Promise.race([subscriptionPromise, timeoutPromise]);
        } catch (error) {

          if (isMounted) {
            setIsSubscriptionLoadingFailed(true);
          }
        }
      } catch (error) {

        if (isMounted) {
          setIsLoadingProjects(false);
        }
      }
    };

    loadData();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [refreshSubscription]);

  const handleProjectSubmit = async (data: any) => {
    try {
      // Only proceed if we have a user
      if (!user) {
        toast({
          title: t('new_project_page.error_title'),
          description: t('new_project_page.session_not_found'),
          variant: "destructive"
        });
        navigate("/login");
        return;
      }

      // Check project limits again before creating the project
      await refreshSubscription();
      const currentProjects = await projectsAPI.getProjects();

      if (Array.isArray(currentProjects)) {
        // Filter to only include active projects (exclude completed ones)
        const activeProjects = currentProjects.filter(project => project.status !== "completed");

        if (activeProjects.length >= projectLimit) {
          toast({
            title: t('new_project_page.project_limit_reached'),
            description: t('new_project_page.project_limit_message', { limit: projectLimit }),
            variant: "destructive"
          });
          navigate("/");
          return;
        }
      }

      // Send data to Supabase through API
      await projectsAPI.createProject({
        name: data.name,
        description: data.description,
        location: data.parcel, // Mapping parcel to location
        budget: data.cost_tl, // Mapping cost_tl to budget
        status: data.status,
        start_date: new Date(), // Set current date as start date
        created_by: user.id, // Use the current user's ID
        units: data.units || [], // Pass the units array
        flats: data.flats || 0, // Pass the flats count
        shops: data.shops || 0, // Pass the shops count
        area: data.area || 0 // Pass the total area
      });

      // Refresh subscription so the project count reflects the new project immediately.
      await refreshSubscription();

      // Show success toast
      toast({
        title: t('new_project_page.project_created_successfully'),
        description: t('new_project_page.project_created_description'),
      });

      // Navigate back to home
      navigate("/");
    } catch (error) {
      // Handle structured error codes from API. New model has two:
      //   NO_ACCESS              — premium expired past grace
      //   PROJECT_LIMIT_REACHED  — count >= limit (1 free / 5 premium)
      if (error instanceof Error) {
        const errorCode = error.name || error.message;

        switch (errorCode) {
          case 'SUBSCRIPTION_INFO_ERROR':
            toast({
              title: t('new_project_page.error_title'),
              description: t('new_project_page.subscription_info_error'),
              variant: "destructive"
            });
            return;

          case 'NO_ACCESS':
            navigate("/subscription", {
              state: {
                message: t('new_project_page.premium_project_limit_error')
              }
            });
            return;

          case 'PROJECT_LIMIT_REACHED':
            if (userIsPremium) {
              toast({
                title: t('new_project_page.project_limit_reached'),
                description: t('new_project_page.project_limit_message', { limit: projectLimit }),
                variant: "destructive"
              });
              navigate("/");
            } else {
              navigate("/subscription", {
                state: {
                  message: t('new_project_page.project_limit_message', { limit: projectLimit })
                }
              });
            }
            return;

          default:
            // Handle other errors (like database errors)
            toast({
              title: t('new_project_page.project_creation_failed'),
              description: error.message.startsWith('SUBSCRIPTION_') || error.message === 'NO_ACCESS' || error.message === 'PROJECT_LIMIT_REACHED'
                ? t('new_project_page.general_error')
                : error.message,
              variant: "destructive"
            });
            return;
        }
      }

      // Fallback for unknown errors
      toast({
        title: t('new_project_page.project_creation_failed'),
        description: t('new_project_page.general_error'),
        variant: "destructive"
      });
    }
  };

  // Show loading state when checking subscription or loading projects
  if (isSubscriptionLoading || isLoadingProjects) {
    return (
      <div className="page-container bg-gray-25">
        <ScreenHeader title={t('new_project_page.title')} />
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
        </div>
        <BottomNavigation />
      </div>
    );
  }

  // Handle subscription loading failure
  if (isSubscriptionLoadingFailed) {
    return (
      <div className="page-container bg-gray-25">
        <ScreenHeader title={t('new_project_page.title')} />
        <div className="flex flex-col items-center justify-center h-full p-6 text-center">
          <div className="bg-error-50 rounded-full inline-flex items-center justify-center p-3 mb-4">
            <AlertTriangle className="text-error-600 h-8 w-8" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t('new_project_page.connection_error_title')}</h2>
          <p className="text-gray-500 mb-6">
            {t('new_project_page.connection_error_description')}
          </p>
          <Button className="bg-brand-600 hover:bg-brand-700 text-white" onClick={() => navigate("/")}>
            {t('new_project_page.return_home')}
          </Button>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  if (isAccessDenied) {
    return (
      <div className="page-container bg-gray-25">
        <ScreenHeader title={t('new_project_page.title')} />
        <div className="flex flex-col items-center justify-center h-full p-6 text-center">
          <div className="bg-error-50 rounded-full inline-flex items-center justify-center p-3 mb-4">
            <AlertTriangle className="text-error-600 h-8 w-8" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t('new_project_page.subscription_required_title')}</h2>
          <p className="text-gray-500 mb-6">
            {t('new_project_page.trial_expired_message')}
          </p>

          {/* Mobile store redirect message for PC users */}
          {!isMobilePlatform && (
            <div className="mb-6 w-full max-w-md">
              <MobileStoreRedirect variant="detailed" />
            </div>
          )}

          {isMobilePlatform ? (
            <Button className="bg-brand-600 hover:bg-brand-700 text-white" onClick={() => navigate("/subscription-expired")}>
              {t('new_project_page.view_subscription_plans')}
            </Button>
          ) : (
            <Button variant="outline" onClick={() => navigate("/subscription-expired")}>
              {t('new_project_page.view_subscription_plans')}
            </Button>
          )}
        </div>
        <BottomNavigation />
      </div>
    );
  }

  return (
    <div className="page-container bg-gray-25">
      <ScreenHeader title={t('new_project_page.title')} />

      <div className="main-content pb-20 bg-gray-25">
        <div className="w-full max-w-3xl mx-auto pt-4">
          <ProjectForm onSubmit={handleProjectSubmit} />
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default NewProjectPage;
